/**
 * Type used for setting the skin type condition of different properties, to specify
 * in which case those properties should be used. E.g. only for rashes, for rashes and
 * lesions etc.
 */
export type ConditionType = 'Lesion' | 'Rash' | 'Oral lesion';
export const ConditionTypes: ConditionType[] = [
  'Lesion',
  'Rash',
  'Oral lesion',
] as const;

/**
 * Assert a free-text condition-type name (dynamic DB reference value) is a
 * dermatology `ConditionType` union member. This is an UNCHECKED cast — it does
 * no runtime validation and must only be used where the value is already known
 * to be a dermatology type. It is the single place that encodes that
 * assumption, so when condition types stop being union-constrained only this
 * function needs to change.
 */
export function asConditionType(name: string): ConditionType;
export function asConditionType(
  name: string | undefined,
): ConditionType | undefined;
export function asConditionType(
  name: string | undefined,
): ConditionType | undefined {
  return name as ConditionType | undefined;
}

/** @deprecated Name-based routing was replaced by assigned forms; retained for compatibility. */
export const LEGACY_DERMATOLOGY_CONDITION_TYPES: ConditionType[] = [
  'Lesion',
  'Rash',
];

/** Which condition-type wording a copy takes. */
export type ConditionTypeCopyKind = 'lesion' | 'rash' | 'generic';

/**
 * Only the legacy dermatology types get copy that names them: every other type
 * would have to be declined and capitalised per locale to read as a sentence.
 */
export function conditionTypeCopyKind(
  conditionType?: ConditionType,
): ConditionTypeCopyKind {
  if (conditionType === 'Lesion') {
    return 'lesion';
  }
  if (conditionType === 'Rash') {
    return 'rash';
  }
  return 'generic';
}

/** Which wording a delete confirmation takes. */
export type DeleteConditionCopyKind = 'draft' | ConditionTypeCopyKind;

/**
 * Deleting the only triage deletes the condition itself, so the copy names the
 * condition type.
 *
 * Resolved here rather than at each call site because the desktop alert and the
 * capture patient home offer the same confirmation and must not disagree about
 * what is being deleted.
 */
export function deleteConditionCopyKind(
  hasOnlyOneTriage: boolean,
  conditionType?: ConditionType,
): DeleteConditionCopyKind {
  return hasOnlyOneTriage ? conditionTypeCopyKind(conditionType) : 'draft';
}
